// ** react imports
import React, { useState, useEffect } from 'react';

// ** components imports
import NormalButton from '../components/common/Buttons/NormalButton';

// ** enums imports
import { BUTTON_THEMES } from '../enums/common';

const DynamicKeyValueInput = ({ value, onChange }) => {
  const [pairs, setPairs] = useState(() => {
    if (value) {
      return Object.entries(value).map(([key, val]) => ({
        id: Math.random().toString(36).substring(7),
        key,
        value: typeof val === 'object' ? JSON.stringify(val) : val,
      }));
    }
    return [];
  });

  useEffect(() => {
    // Skip if value is undefined or null
    if (value === undefined || value === null) {
      return;
    }

    // Convert current pairs to object for comparison
    const currentPairsAsObject = {};
    pairs.forEach(pair => {
      if (pair.key) {
        try {
          currentPairsAsObject[pair.key] = JSON.parse(pair.value);
        } catch (e) {
          currentPairsAsObject[pair.key] = pair.value;
        }
      }
    });

    // Compare the objects deeply
    const currentValueString = JSON.stringify(value);
    const currentPairsString = JSON.stringify(currentPairsAsObject);

    // Only update if there's an actual difference
    if (currentValueString !== currentPairsString) {
      // Create a mapping of existing pairs by key for preservation
      const existingPairsMap = new Map();
      pairs.forEach(pair => {
        if (pair.key) {
          existingPairsMap.set(pair.key, pair);
        }
      });

      // Create new pairs preserving IDs for existing keys
      const newInitialPairs = Object.entries(value || {}).map(([key, val]) => {
        const existingPair = existingPairsMap.get(key);
        if (existingPair) {
          // Preserve existing ID if key exists
          return {
            ...existingPair,
            key,
            value: typeof val === 'object' ? JSON.stringify(val) : val,
          };
        } else {
          // Create new pair with new ID
          return {
            id: Math.random().toString(36).substring(7),
            key,
            value: typeof val === 'object' ? JSON.stringify(val) : val,
          };
        }
      });
      setPairs(newInitialPairs);
    }
  }, [value]);

  const handlePairChange = (id, field, newValue) => {
    setPairs(prevPairs =>
      prevPairs.map(pair =>
        pair.id === id ? { ...pair, [field]: newValue } : pair
      )
    );
  };

  const addPair = () => {
    setPairs(prevPairs => [
      ...prevPairs,
      { id: Math.random().toString(36).substring(7), key: '', value: '' },
    ]);
  };

  const removePair = id => {
    setPairs(prevPairs => prevPairs.filter(pair => pair.id !== id));
  };

  useEffect(() => {
    // Skip if onChange is not a function
    if (typeof onChange !== 'function') {
      return;
    }

    const newMetadata = {};
    pairs.forEach(pair => {
      if (pair.key) {
        try {
          newMetadata[pair.key] = JSON.parse(pair.value);
        } catch (e) {
          newMetadata[pair.key] = pair.value;
        }
      }
    });
    onChange(newMetadata);
  }, [pairs, onChange]);

  return (
    <div className="space-y-4 h-full">
      {pairs.map(pair => (
        <div key={pair.id} className="flex items-center space-x-2">
          <input
            type="text"
            className="form-input w-1/3"
            placeholder="Key"
            value={pair.key}
            onChange={e => handlePairChange(pair.id, 'key', e.target.value)}
          />
          <input
            type="text"
            className="form-input w-2/3"
            placeholder="Value"
            value={pair.value}
            onChange={e => handlePairChange(pair.id, 'value', e.target.value)}
          />
          <NormalButton
            type="button"
            theme={BUTTON_THEMES.DANGER}
            className="btn-md"
            onClick={() => removePair(pair.id)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              width={19}
              height={19}
            >
              <path d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20ZM12 10.5858L14.8284 7.75736L16.2426 9.17157L13.4142 12L16.2426 14.8284L14.8284 16.2426L12 13.4142L9.17157 16.2426L7.75736 14.8284L10.5858 12L7.75736 9.17157L9.17157 7.75736L12 10.5858Z"></path>
            </svg>
          </NormalButton>
        </div>
      ))}
      <div
        className={
          pairs.length === 0
            ? 'flex justify-center items-center h-full mt-8'
            : 'flex justify-end'
        }
      >
        <NormalButton
          type="button"
          theme={BUTTON_THEMES.TERTIARY}
          onClick={addPair}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            width={22}
            height={22}
            className="mr-2"
          >
            <path d="M11 11V7H13V11H17V13H13V17H11V13H7V11H11ZM12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20Z"></path>
          </svg>
          Add Item
        </NormalButton>
      </div>
    </div>
  );
};

export default DynamicKeyValueInput;
